import { createSlice } from "@reduxjs/toolkit"
import authAPI from "./authAPI"
import { createAppAsyncThunk } from "app/hooks"
import { IAuthState, ILoginWithEmailPayload } from "./types"
import { resetStoreAction } from "config"
import { jwtDecode } from "jwt-decode"
import dayjs from "dayjs"
import { exchangeRefreshToken } from "api/refreshToken"
import { IProfile } from "features/profile/profileTypes"
import { is404Response, isAuthError } from "utils"
import { notifyAppToLogOut } from "utils/nativeAppHelper"
import * as Sentry from "@sentry/react"
import { getNowUtcIso } from "utils/dateTime"

const getInfoFromIdToken = (idToken: string) => {
  const decoded: any = jwtDecode(idToken)
  return {
    userId: decoded["custom:user_id"],
    email: decoded["email"],
  }
}

export const loginFromApp = createAppAsyncThunk(
  "auth/loginFromApp",
  async (refreshToken: string, { rejectWithValue, getState, dispatch }) => {
    // Case 2 early signal: native app is sending a different token than what the store holds,
    // meaning a refreshToken call may be in-flight with the old token concurrently
    const currentRefreshToken = getState().auth.refreshToken
    const isTokenMismatch =
      currentRefreshToken && currentRefreshToken !== refreshToken

    if (isTokenMismatch) {
      const authState = getState().auth
      Sentry.withScope((scope) => {
        scope.setFingerprint(["LoginFromApp", "monitored-before"])
        scope.setContext("login-state", {
          timestamp: getNowUtcIso(),
          firstLoginTime: authState.firstLoginTime,
          lastLoginTime: authState.lastLoginTime,
          firstLogoutTime: authState.firstLogoutTime,
          lastLogoutTime: authState.lastLogoutTime,
          loginNumber: authState.loginNumber,
          isTokenMismatch,
        })
        Sentry.captureMessage(
          "[LoginFromApp/before] Case2: token mismatch (store ≠ login token)",
          "warning",
        )
      })
    }

    const reloginAuthState = getState().auth
    const isReloginAfterRefreshFailure =
      !currentRefreshToken &&
      reloginAuthState.refreshTokenFailedCount >
        reloginAuthState.logoutNotifyCount

    if (isReloginAfterRefreshFailure) {
      Sentry.withScope((scope) => {
        scope.setFingerprint(["LoginFromApp", "monitored-before"])
        scope.setContext("login-state", {
          timestamp: getNowUtcIso(),
          refreshTokenFailedCount: reloginAuthState.refreshTokenFailedCount,
          logoutNotifyCount: reloginAuthState.logoutNotifyCount,
          loginNumber: reloginAuthState.loginNumber,
          firstLoginTime: reloginAuthState.firstLoginTime,
          lastLoginTime: reloginAuthState.lastLoginTime,
        })
        Sentry.captureMessage(
          "[LoginFromApp/before] re-login attempt after refresh failure",
          "info",
        )
      })
    }

    try {
      const response = await exchangeRefreshToken(refreshToken)
      if (isTokenMismatch) {
        Sentry.withScope((scope) => {
          scope.setFingerprint(["LoginFromApp", "monitored-success"])
          scope.setContext("login-state", {
            timestamp: getNowUtcIso(),
            loginNumber: reloginAuthState.loginNumber,
            lastLoginTime: reloginAuthState.lastLoginTime,
          })
          Sentry.captureMessage(
            "[LoginFromApp/success] Case2: token mismatch resolved",
            "info",
          )
        })
      }
      if (isReloginAfterRefreshFailure) {
        Sentry.withScope((scope) => {
          scope.setFingerprint(["LoginFromApp", "monitored-success"])
          scope.setContext("login-state", {
            timestamp: getNowUtcIso(),
            refreshTokenFailedCount: reloginAuthState.refreshTokenFailedCount,
            logoutNotifyCount: reloginAuthState.logoutNotifyCount,
            loginNumber: reloginAuthState.loginNumber,
          })
          Sentry.captureMessage(
            "[LoginFromApp/success] re-login after refresh failure",
            "info",
          )
        })
      }
      return {
        accessToken: response.data.accessToken,
        refreshToken,
        idToken: response.data.idToken,
      }
    } catch (err: any) {
      if (!isAuthError(err)) return rejectWithValue(err)

      const authState = getState().auth
      const isAlreadyNotifyLogout = authState.logoutNotifyCount >= 1
      const daysSinceFirstLogin = authState.firstLoginTime
        ? dayjs().diff(dayjs(authState.firstLoginTime), "day")
        : null
      const tooSoonExpiry =
        daysSinceFirstLogin !== null && daysSinceFirstLogin < 20

      if (
        isReloginAfterRefreshFailure ||
        isAlreadyNotifyLogout ||
        tooSoonExpiry
      ) {
        Sentry.withScope((scope) => {
          scope.setFingerprint(["LoginFromApp", "monitored-failure"])
          scope.setContext("login-state", {
            timestamp: getNowUtcIso(),
            firstLoginTime: authState.firstLoginTime,
            lastLoginTime: authState.lastLoginTime,
            firstLogoutTime: authState.firstLogoutTime,
            lastLogoutTime: authState.lastLogoutTime,
            refreshTokenFailedCount: authState.refreshTokenFailedCount,
            logoutNotifyCount: authState.logoutNotifyCount,
            daysSinceFirstLogin,
            isReloginAfterRefreshFailure,
            isAlreadyNotifyLogout,
            tooSoonExpiry,
          })
          const cases = [
            isReloginAfterRefreshFailure && "re-login after refresh failure",
            isAlreadyNotifyLogout &&
              `Case1: stale (logoutNotifyCount: ${authState.logoutNotifyCount})`,
            tooSoonExpiry &&
              `Case3: early expiry (${daysSinceFirstLogin}d since first login)`,
          ]
            .filter(Boolean)
            .join(" + ")
          Sentry.captureMessage(`[LoginFromApp/failure] ${cases}`, "warning")
        })
      } else {
        Sentry.withScope((scope) => {
          scope.setFingerprint(["LoginFromApp", "unmonitored-failure"])
          scope.setContext("login-state", {
            timestamp: getNowUtcIso(),
            firstLoginTime: authState.firstLoginTime,
            lastLoginTime: authState.lastLoginTime,
            refreshTokenFailedCount: authState.refreshTokenFailedCount,
            logoutNotifyCount: authState.logoutNotifyCount,
          })

          Sentry.captureMessage(
            "[LoginFromApp/normal-failure] unmonitored case",
            "info",
          )
        })
      }

      await Sentry.flush(2000)

      dispatch(incrementLogoutNotifyCount())
      notifyAppToLogOut(refreshToken)
      return rejectWithValue(null)
    }
  },
)

export const loginWithEmail = createAppAsyncThunk(
  "auth/loginWithEmail",
  async (payload: ILoginWithEmailPayload, { rejectWithValue }) => {
    try {
      const response = await authAPI.loginWithEmail(payload)
      return { ...response, email: payload.email }
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const getFitProfile = createAppAsyncThunk(
  "auth/getFitProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await authAPI.getFitProfile()
      return response
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const createFitProfile = createAppAsyncThunk(
  "auth/createFitProfile",
  async (
    {
      profile,
      isSubscribeUpdate,
    }: { profile: IProfile; isSubscribeUpdate: boolean },
    { rejectWithValue, getState },
  ) => {
    const email = getState().auth.email
    try {
      await authAPI.createAccount()
      await authAPI.createFitProfile(profile)
      if (!isSubscribeUpdate) {
        await authAPI.updateEmailSubscription({
          email,
          subscriptionState: "OPTED_IN",
        })
      }

      return profile
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

const initialState: IAuthState = {
  getFitProfileLoading: false,
  getFitProfileFailed: undefined,
  fitProfile: undefined,
  isNotHasFitProfileYet: false,

  createFitProfileLoading: false,
  createFitProfileFailed: undefined,
  createFitProfileSuccess: undefined,

  loginFromAppLoading: true,
  loginFromAppFailed: null,
  token: "",
  refreshToken: "",

  email: "",
  userId: "",

  loginWithEmailLoading: false,
  loginWithEmailFailed: null,
  loginWithEmailSuccess: null,

  prevEmailPayload: {
    email: "",
    password: "",
  },

  firstLoginTime: "",
  lastLoginTime: "",
  firstLogoutTime: "",
  lastLogoutTime: "",
  loginNumber: 0,
  refreshTokenFailedCount: 0,
  logoutNotifyCount: 0,
}

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    updateLocalEmail(state, { payload }) {
      state.email = payload
    },
    onLogOut() {
      return initialState
    },

    updateToken(state, { payload: token }) {
      state.token = token
    },

    incrementLogoutNotifyCount(state) {
      state.logoutNotifyCount += 1
      if (!state.firstLogoutTime) {
        state.firstLogoutTime = getNowUtcIso()
      }
      state.lastLogoutTime = getNowUtcIso()
    },

    incrementRefreshTokenFailed(state) {
      state.refreshTokenFailedCount += 1
    },
  },
  extraReducers(builder) {
    builder
      .addCase(createFitProfile.pending, (state) => {
        state.createFitProfileLoading = true
      })
      .addCase(createFitProfile.fulfilled, (state, { payload }) => {
        state.createFitProfileLoading = false
        state.createFitProfileSuccess = payload
        state.fitProfile = payload
        state.getFitProfileFailed = undefined
      })
      .addCase(createFitProfile.rejected, (state, { payload }) => {
        state.createFitProfileLoading = false
        state.createFitProfileFailed = payload
      })

      .addCase(getFitProfile.pending, (state) => {
        state.getFitProfileLoading = true
      })
      .addCase(getFitProfile.fulfilled, (state, { payload }) => {
        state.getFitProfileLoading = false
        state.fitProfile = payload
      })
      .addCase(getFitProfile.rejected, (state, { payload }) => {
        state.getFitProfileLoading = false
        state.getFitProfileFailed = is404Response(payload) ? undefined : payload
        state.isNotHasFitProfileYet = is404Response(payload)
      })

      .addCase(loginFromApp.fulfilled, (state, { payload }) => {
        state.loginFromAppLoading = false
        state.token = payload.accessToken
        state.refreshToken = payload.refreshToken
        const { userId, email } = getInfoFromIdToken(payload.idToken)
        state.userId = userId
        state.email = email
        state.loginNumber = state.loginNumber + 1
        state.lastLoginTime = getNowUtcIso()

        if (!state.firstLoginTime) {
          state.firstLoginTime = getNowUtcIso()
        }
      })

      .addCase(loginFromApp.rejected, (state, { payload }) => {
        state.loginFromAppFailed = payload
        state.loginFromAppLoading = false
      })

      .addCase(loginWithEmail.pending, (state, { meta }) => {
        state.loginWithEmailLoading = true
        state.loginWithEmailFailed = undefined
        state.prevEmailPayload = meta.arg
      })
      .addCase(loginWithEmail.fulfilled, (state, { payload }) => {
        state.loginWithEmailLoading = false
        state.email = payload.email

        if (payload.token) {
          state.token = payload.token.accessToken
          state.refreshToken = payload.token.refreshToken
          const { userId, email } = getInfoFromIdToken(payload.token.idToken)
          state.userId = userId
          state.email = email
        }
      })
      .addCase(loginWithEmail.rejected, (state, { payload }) => {
        state.loginWithEmailLoading = false
        state.loginWithEmailFailed = payload
      })

      .addCase(resetStoreAction, (state) => {
        const {
          firstLoginTime,
          lastLoginTime,
          firstLogoutTime,
          lastLogoutTime,
          refreshTokenFailedCount,
          loginNumber,
          logoutNotifyCount,
        } = state
        return {
          ...initialState,
          firstLoginTime,
          lastLoginTime,
          firstLogoutTime,
          lastLogoutTime,
          refreshTokenFailedCount,
          loginNumber,
          logoutNotifyCount,
        }
      })
  },
})

export const {
  updateToken,
  onLogOut,
  updateLocalEmail,
  incrementLogoutNotifyCount,
  incrementRefreshTokenFailed,
} = authSlice.actions

export default authSlice.reducer
